export type ItemStatus = 'available' | 'returned' | 'discarded';
export type ClaimStatus = 'open' | 'matched' | 'closed';

export interface ItemImage {
  id: number;
  item_id: number;
  zipline_url: string;
  created_at: string;
}

export interface Item {
  id: number;
  cover_image?: string | null;
  title: string;
  category_id: string;
  color: string | null;
  brand: string | null;
  distinctive_features: string | null;
  found_date: string;
  discard_after: string | null;
  storage_location: string | null;
  notes: string | null;
  status: ItemStatus;
  created_at: string;
  updated_at: string;
}

export interface ItemWithImages extends Item {
  images: ItemImage[];
}

export interface LostClaimImage {
  id: number;
  claim_id: number;
  zipline_url: string;
  created_at: string;
}

export interface LostClaim {
  id: number;
  cover_image?: string | null;
  category_id: string | null;
  description: string;
  color: string | null;
  brand: string | null;
  distinctive_features: string | null;
  lost_date: string | null;
  contact_name: string;
  contact_method: string;
  social_profile: string | null;
  matched_item_id: number | null;
  status: ClaimStatus;
  created_at: string;
}

export interface LostClaimWithImages extends LostClaim {
  images: LostClaimImage[];
  matchedItem: Item | null;
}

export interface MatchConfig {
  threshold: number;
  colorExact: number;
  colorPartial: number;
  brandExact: number;
  brandPartial: number;
  keywordMax: number;
  keywordMinLength: number;
  stopWords: string[];
}

export const DEFAULT_MATCH_CONFIG: MatchConfig = {
  threshold: 3,
  colorExact: 4,
  colorPartial: 2,
  brandExact: 4,
  brandPartial: 2,
  keywordMax: 4,
  keywordMinLength: 3,
  stopWords: [
    'og', 'er', 'en', 'et', 'den', 'det', 'de', 'med', 'for', 'til', 'på', 'af', 'i',
    'the', 'and', 'or', 'a', 'an', 'in', 'of', 'with', 'to',
  ],
};

export type SuggestionStatus = 'pending' | 'confirmed' | 'rejected';

export interface Suggestion {
  id: number;
  item_id: number;
  claim_id: number;
  score: number;
  status: SuggestionStatus;
  created_at: string;
  item: Item & { cover_image: string | null };
  claim: LostClaim & { cover_image: string | null };
}

export type CreateItemInput = Omit<Item, 'id' | 'created_at' | 'updated_at' | 'status' | 'cover_image'> & {
  status?: ItemStatus;
};

export type CreateClaimInput = Omit<
  LostClaim,
  'id' | 'created_at' | 'status' | 'matched_item_id'
> & {
  matched_item_id?: number | null;
};

export interface CategoryEntry {
  id: string;
  label_da: string;
  group: string;
  synonyms: string[];
}

export interface CategoryGroup {
  id: string;
  label_da: string;
}

export interface ColorEntry {
  id: string;
  label_da: string;
  synonyms: string[];
}

export interface CategoriesData {
  categories: CategoryEntry[];
  category_groups: CategoryGroup[];
  colors: ColorEntry[];
  patterns: { id: string; label_da: string }[];
  attributes: { id: string; label_da: string; synonyms: string[] }[];
  brands: Record<string, string[]>;
}
